// src/view/pages/AddSitePage.tsx
import React, { useState, startTransition } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Input,
    Box,
    Grid,
    IconButton,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Tabs,
    Tab,
    DialogTitle,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, ArrowBack } from '@mui/icons-material';
import { HttpResponse, HTTPValidationError, Site, SiteCreate, BulkSite } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import clientService from 'src/clientService';
import { Map } from '@mui/icons-material';
import MapBoxDrawer from '../DMap/MapBoxDrawer';
import SitePreviewMap, { PreviewPoint } from '../DMap/SitePreviewMap';
import { ParsedFeatureData } from 'src/mapbox-helpers/map-types';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';

interface AddSitePageParams extends Record<string, string | undefined> {
    id: string;
}

interface InvalidRow {
    rowNumber: number;
    content: string;
    reason: string;
}

const AddSitePage: React.FC = () => {
    const { id: projectId } = useParams<AddSitePageParams>();
    const { state, dispatch } = useAppState();

    const navigate = useNavigate();
    const { showSnackbar, snackbarType } = useSnackbar();
    const { selectedProject } = state;
    const { projectDetails } = selectedProject || {};

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
    const [formData, setFormData] = useState<SiteCreate>({
        site_name: '',
        shapefile_data: '',
        carbon_analysis_enabled: false,
        bio_analysis_enabled: false,
        env_analysis_enabled: false,
        boundary: null,
        centroid_wkt: null,
        area: null,
    } as unknown as SiteCreate); // cast because SiteCreate may not accept nulls exactly

    const [fileName, setFileName] = useState<string>('');
    const [csvFileName, setCsvFileName] = useState<string>('');
    const [csvData, setCsvData] = useState<string>('');
    const [previewPoints, setPreviewPoints] = useState<PreviewPoint[]>([]);
    const [validationErrors, setValidationErrors] = useState<InvalidRow[]>([]);
    const [submissionErrors, setSubmissionErrors] = useState<InvalidRow[]>([]);
    const [errorDialog, setErrorDialog] = useState<{ open: boolean; title: string; rows: InvalidRow[] }>({ open: false, title: '', rows: [] });
    const [envAnalysisTab, setEnvAnalysisTab] = useState<'single' | 'bulk'>('single');
    const [isDrawBoundaryDialogOpen, setIsDrawBoundaryDialogOpen] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [name]: name === 'value' ? (value === '' ? null : parseFloat(value)) : value,
        }));
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        setFormData((prev: any) => {
            const newState = { ...prev, [name]: checked };

            if (checked) {
                if (name === 'env_analysis_enabled') {
                    newState.carbon_analysis_enabled = false;
                    newState.bio_analysis_enabled = false;
                    newState.shapefile_data = '';
                    newState.boundary = null;
                    setFileName('');
                } else if (name === 'carbon_analysis_enabled' || name === 'bio_analysis_enabled') {
                    newState.env_analysis_enabled = false;
                    setEnvAnalysisTab('single');
                    setCsvData('');
                    setPreviewPoints([]);
                    setValidationErrors([]);
                    setSubmissionErrors([]);
                    setCsvFileName('');
                }
            }

            return newState;
        });
    };

    // Centralized file processing logic
    const processFile = (file: File | null | undefined) => {
        if (file) {
            if (file.type !== 'application/zip' && !file.name.endsWith('.zip')) {
                showSnackbar({ message: 'Invalid file type. Please upload a .zip file.', type: snackbarType.ERROR });
                setFileName('');
                setFormData((prev: any) => ({ ...prev, shapefile_data: '' }));
                const fileInput = document.getElementById('fileInput-hidden') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
                return false;
            }
            setFileName(file.name);
            const reader = new FileReader();
            reader.onload = () => {
                setFormData((prev: any) => ({ ...prev, shapefile_data: reader.result as string }));
            };
            reader.onerror = () => {
                showSnackbar({ message: 'Error reading file.', type: snackbarType.ERROR });
                setFileName('');
                const fileInput = document.getElementById('fileInput-hidden') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
            };
            reader.readAsDataURL(file);
            return true;
        } else {
            setFileName('');
            setFormData((prev: any) => ({ ...prev, shapefile_data: '' }));
            return false;
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        processFile(event.target.files?.[0]);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isSubmitting) setIsDraggingOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);
        if (isSubmitting) return;
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            processFile(event.dataTransfer.files[0]);
            event.dataTransfer.clearData();
        }
    };

    const clearFileSelection = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (event) event.stopPropagation();
        setFileName('');
        setFormData((prev: any) => ({ ...prev, shapefile_data: '' }));
        const fileInput = document.getElementById('fileInput-hidden') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const processCsvFile = (file: File | null | undefined) => {
        if (!file) {
            setCsvFileName('');
            setCsvData('');
            setPreviewPoints([]);
            setValidationErrors([]);
            setSubmissionErrors([]);
            return;
        }

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            showSnackbar({ message: 'Invalid file type. Please upload a .csv file.', type: snackbarType.ERROR });
            setCsvFileName('');
            setCsvData('');
            setPreviewPoints([]);
            setValidationErrors([]);
            setSubmissionErrors([]);
            const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
            return;
        }

        setCsvFileName(file.name);
        const reader = new FileReader();

        reader.onload = (e) => {
            const text = e.target?.result as string;
            if (!text) {
                showSnackbar({ message: 'File is empty or could not be read.', type: snackbarType.ERROR });
                return;
            }
            setValidationErrors([]);
            setSubmissionErrors([]);

            try {
                const allLines = text.trim().split('\n');
                const headerLine = allLines[0];
                let dataRows = allLines.slice(1);

                if (dataRows.length > 15) {
                    showSnackbar({
                        message: 'Processing the first 15 rows. Please upload remaining rows in a separate file.',
                        type: snackbarType.INFO,
                    });
                    dataRows = dataRows.slice(0, 15);
                }

                const processedCsvText = [headerLine, ...dataRows].join('\n');
                setCsvData(processedCsvText);

                // Parse for preview
                const header = headerLine.split(';').map((h) => h.trim());
                const nameIndex = header.indexOf('name');
                const latIndex = header.indexOf('lat');
                const longIndex = header.indexOf('long');
                const valueIndex = header.indexOf('value'); // may be -1
                const typeIndex = header.indexOf('type'); // may be -1

                if (nameIndex === -1 || latIndex === -1 || longIndex === -1) {
                    showSnackbar({ message: 'CSV header is invalid. It must contain "name", "lat", and "long" separated by semicolons.', type: snackbarType.ERROR });
                    setPreviewPoints([]);
                    return;
                }

                const validPoints: PreviewPoint[] = [];
                const currentValidationErrors: InvalidRow[] = [];

                dataRows.forEach((line, index) => {
                    const rowNumber = index + 2; // header + 1-indexed
                    if (!line.trim()) {
                        currentValidationErrors.push({ rowNumber, content: '(Empty Line)', reason: 'Row is empty.' });
                        return;
                    }

                    const values = line.split(';');
                    const siteName = values[nameIndex]?.trim();
                    const latStr = values[latIndex]?.trim();
                    const longStr = values[longIndex]?.trim();
                    const valueStr = valueIndex !== -1 ? values[valueIndex]?.trim() : undefined;
                    const typeStr = typeIndex !== -1 ? values[typeIndex]?.trim() : undefined;

                    const lat = parseFloat(latStr);
                    const long = parseFloat(longStr);
                    const value: number | undefined = valueStr === undefined || valueStr === '' ? undefined : Number(valueStr);
                    const type: string | undefined = typeStr === undefined || typeStr === '' ? undefined : typeStr;

                    const errors: string[] = [];
                    if (!siteName) errors.push('Missing site name.');
                    if (!latStr || isNaN(lat)) errors.push('Invalid or missing latitude.');
                    if (!longStr || isNaN(long)) errors.push('Invalid or missing longitude.');
                    if (valueStr !== undefined && valueStr !== '' && isNaN(value as number)) {
                        errors.push('Invalid numeric value.');
                    }

                    if (errors.length > 0) {
                        currentValidationErrors.push({ rowNumber, content: line, reason: errors.join(' ') });
                    } else {
                        validPoints.push({ name: siteName, lat, long, value, type });
                    }
                });

                setPreviewPoints(validPoints);
                setValidationErrors(currentValidationErrors);
            } catch (error) {
                showSnackbar({ message: 'Failed to parse CSV file.', type: snackbarType.ERROR });
                setPreviewPoints([]);
                setValidationErrors([]);
            }
        };

        reader.onerror = () => {
            showSnackbar({ message: 'Error reading file.', type: snackbarType.ERROR });
            setCsvFileName('');
            setCsvData('');
            setValidationErrors([]);
            setSubmissionErrors([]);
            const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
        };

        reader.readAsText(file);
    };

    const handleCsvFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        processCsvFile(event.target.files?.[0]);
    };

    const handleCsvDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);
        if (isSubmitting) return;
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            processCsvFile(event.dataTransfer.files[0]);
            event.dataTransfer.clearData();
        }
    };

    const clearCsvFileSelection = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (event) event.stopPropagation();
        setCsvFileName('');
        setCsvData('');
        setPreviewPoints([]);
        setValidationErrors([]);
        setSubmissionErrors([]);
        const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const handleOpenDrawBoundaryDialog = () => {
        setIsDrawBoundaryDialogOpen(true);
    };

    const handleCloseDrawBoundaryDialog = () => {
        setIsDrawBoundaryDialogOpen(false);
    };

    const handleSubmitDrawBoundary = () => {
        const isPoint = formData.env_analysis_enabled && projectDetails?.has_environmental_module && envAnalysisTab === 'single';
        if (formData.boundary) {
            showSnackbar({
                message: 'Boundary saved successfully!',
                type: snackbarType.SUCCESS,
            });
        } else if (formData.centroid_wkt) {
            showSnackbar({
                message: isPoint ? 'Point location saved successfully!' : 'Boundary saved successfully!',
                type: snackbarType.SUCCESS,
            });
        }
        handleCloseDrawBoundaryDialog();
    };

    const handleDownloadSampleCsv = () => {
        const csvContent = "data:text/csv;charset=utf-8,name;lat;long;value;type\nMumbai;19.0760;72.8777;250000;urban\nDelhi;28.7041;77.1025;350000;urban\nBangalore;12.9716;77.5946;200000;urban";
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "sample_sites_bulk_upload.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const duplicateSiteName = (siteName: string): boolean => {
        if (!projectDetails || !projectDetails.sites) return false;
        return projectDetails.sites.some((site: Site) => site.site_name.toLocaleLowerCase() === siteName.toLocaleLowerCase());
    };

    const handleSubmit = async () => {
        if (!projectId || !projectDetails || projectDetails.project_id !== projectId) {
            showSnackbar({ message: 'Project information is missing or mismatched. Cannot add site.', type: snackbarType.ERROR });
            return;
        }

        const isCarbonOrBio = (formData as any).carbon_analysis_enabled || (formData as any).bio_analysis_enabled;
        const isEnv = (formData as any).env_analysis_enabled;
        const isEnvBulk = isEnv && envAnalysisTab === 'bulk';

        if (!isCarbonOrBio && !isEnv) {
            showSnackbar({ message: 'Please select an analysis type.', type: snackbarType.ERROR });
            return;
        }

        if (isEnvBulk) {
            if (!csvData) {
                showSnackbar({ message: 'Please upload a CSV file for bulk processing.', type: snackbarType.ERROR });
                return;
            }
            handleBulkSubmit();
            return;
        }

        if (!((formData as any).site_name || '').trim()) {
            showSnackbar({ message: 'Please fill in Site Name.', type: snackbarType.ERROR });
            return;
        }

        if (duplicateSiteName(((formData as any).site_name || '').trim())) {
            showSnackbar({ message: 'Site name already exists. Please choose a different name.', type: snackbarType.ERROR });
            return;
        }

        if (!((formData as any).shapefile_data || (formData as any).boundary || (formData as any).centroid_wkt)) {
            showSnackbar({ message: 'Please upload a Shapefile or draw a boundary.', type: snackbarType.ERROR });
            return;
        }

        if (isEnv && !(formData as any).boundary && (formData as any).centroid_wkt) {
            const { boundary, area } = MapboxFeature.createCircleAndGetData((formData as any).centroid_wkt);
            (formData as any).boundary = boundary || '';
            (formData as any).area = area || 0;
        }

        setIsSubmitting(true);
        clientService.api.createSiteApiV1ProjectsProjectIdSitesPost(projectId, formData).then((response: HttpResponse<Site, HTTPValidationError>) => {
            showSnackbar({ message: 'Site added successfully!' });
            dispatch({
                type: 'SET_PROJECT_DETAILS',
                payload: {
                    ...projectDetails,
                    sites: [...(projectDetails.sites || []), response.data],
                },
            });
            startTransition(() => {
                navigate(`/project/${projectId}/sites`);
            });
        }).catch(() => {
            showSnackbar({
                message: 'Error adding site:',
                type: snackbarType.ERROR,
            });
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const onCompleteDraw = (data: ParsedFeatureData | null) => {
        if (!data) return;
        setFormData((prev: any) => ({ ...prev, ...data }));
    };

    const handleBulkSubmit = async () => {
        if (previewPoints.length === 0) {
            showSnackbar({ message: 'No valid sites to submit.', type: snackbarType.WARNING });
            return;
        }

        setIsSubmitting(true);
        setSubmissionErrors([]);

        const sitesToCreate: BulkSite[] = previewPoints
            .filter((p) => !duplicateSiteName(p.name))
            .map((p) => {
                const { boundary, area } = MapboxFeature.createCircleAndGetData(`POINT (${p.long} ${p.lat})`);
                const siteObj: any = {
                    site_name: p.name,
                    centroid_wkt: `POINT (${p.long} ${p.lat})`,
                    env_analysis_enabled: true,
                    boundary: boundary || '',
                    area: area || 0,
                };
                if (p.value !== undefined) siteObj.value = p.value;
                siteObj.type = p.type !== undefined ? p.type : "";
                if (p.type !== undefined) siteObj.type = p.type;
                return siteObj as BulkSite;
            });

        if (sitesToCreate.length < previewPoints.length) {
            const skippedCount = previewPoints.length - sitesToCreate.length;
            showSnackbar({ message: `Skipping ${skippedCount} site(s) that already exist in this project.`, type: snackbarType.INFO });
        }

        if (sitesToCreate.length === 0) {
            showSnackbar({ message: 'No new sites to add. All valid sites from the CSV may already exist.', type: snackbarType.WARNING });
            setIsSubmitting(false);
            return;
        }

        clientService.api.createBulkSiteApiV1ProjectsProjectIdSitesBulkPost(projectId!, sitesToCreate).then((response: HttpResponse<Site[], HTTPValidationError>) => {
            showSnackbar({ message: 'Site added successfully!' });
            startTransition(() => {
                dispatch({ type: 'SET_PROJECT_DETAILS', payload: { ...projectDetails!, sites: [...(projectDetails!.sites || []), ...response.data] } });
            });
            startTransition(() => {
                navigate(`/project/${projectId}/sites`);
            });
        }).catch(() => {
            showSnackbar({
                message: 'Error adding site:',
                type: snackbarType.ERROR,
            });
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={() => navigate(-1)} color="primary" sx={{ mr: 1 }} aria-label="Back to project sites" disabled={isSubmitting}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1">Add Site to {projectDetails?.project_name}</Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            required={!(formData.env_analysis_enabled && envAnalysisTab === 'bulk')}
                            fullWidth
                            label="Site Name"
                            name="site_name"
                            value={(formData as any).site_name}
                            onChange={handleChange}
                            disabled={!!(isSubmitting || (formData.env_analysis_enabled && envAnalysisTab === 'bulk'))}
                            helperText={(formData.env_analysis_enabled && envAnalysisTab === 'bulk') ? "Site names will be taken from the uploaded CSV file." : ""}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>Analysis Types</Typography>
                        <Grid container>
                            {projectDetails?.has_biodiversity_module && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControlLabel
                                        control={<Checkbox checked={!!(formData as any).bio_analysis_enabled} onChange={handleCheckboxChange} name="bio_analysis_enabled" disabled={isSubmitting || !!(formData as any).env_analysis_enabled} />}
                                        label="Biodiversity Analysis"
                                    />
                                </Grid>
                            )}
                            {projectDetails?.has_carbon_module && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControlLabel
                                        control={<Checkbox checked={!!(formData as any).carbon_analysis_enabled} onChange={handleCheckboxChange} name="carbon_analysis_enabled" disabled={isSubmitting || !!(formData as any).env_analysis_enabled} />}
                                        label="Carbon Analysis"
                                    />
                                </Grid>
                            )}
                            {projectDetails?.has_environmental_module && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControlLabel
                                        control={<Checkbox checked={!!(formData as any).env_analysis_enabled} onChange={handleCheckboxChange} name="env_analysis_enabled" disabled={isSubmitting || !!(formData as any).carbon_analysis_enabled || !!(formData as any).bio_analysis_enabled} />}
                                        label="Climate Analysis"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {(formData.carbon_analysis_enabled || formData.bio_analysis_enabled) && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>Shapefile (.zip)</Typography>
                            <Box
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => {
                                    if (!isSubmitting) document.getElementById('fileInput-hidden')?.click();
                                }}
                                sx={{
                                    border: (theme) => `2px dashed ${isDraggingOver ? theme.palette.primary.main : theme.palette.divider}`,
                                    padding: 3,
                                    textAlign: 'center',
                                    cursor: isSubmitting ? 'default' : 'pointer',
                                    backgroundColor: (theme) => isDraggingOver ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                                    mb: 2,
                                    opacity: isSubmitting ? 0.7 : 1,
                                }}
                            >
                                <Input
                                    id="fileInput-hidden"
                                    type="file"
                                    inputProps={{ accept: '.zip' }}
                                    onChange={handleFileInputChange}
                                    sx={{ display: 'none' }}
                                    disabled={isSubmitting}
                                />
                                {fileName ? (
                                    <>
                                        <Typography variant="body1" color="textSecondary">Selected file: {fileName}</Typography>
                                        <Button size="small" onClick={clearFileSelection} sx={{ mt: 1 }} disabled={isSubmitting}>Clear selection</Button>
                                    </>
                                ) : (
                                    <Typography variant="body1" color="textSecondary">Drag & drop a .zip file here, or click to select</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                                <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                                <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>OR</Typography>
                                <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                            </Box>
                            <Button fullWidth variant="outlined" startIcon={<Map />} onClick={handleOpenDrawBoundaryDialog} disabled={isSubmitting} sx={{ mb: 2 }}>
                                Draw Boundary on Map
                            </Button>
                        </Grid>
                    )}

                    {formData.env_analysis_enabled && projectDetails?.has_environmental_module && (
                        <Grid item xs={12}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={envAnalysisTab} onChange={(e, newValue) => setEnvAnalysisTab(newValue as 'single' | 'bulk')} aria-label="climate analysis type">
                                    <Tab label="Single Site" value="single" />
                                    <Tab label="Bulk Upload" value="bulk" />
                                </Tabs>
                            </Box>
                            <Box sx={{ pt: 3 }}>
                                {envAnalysisTab === 'single' && (
                                    <>
                                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>Shapefile (.zip)</Typography>
                                        <Box
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            onClick={() => {
                                                if (!isSubmitting) document.getElementById('fileInput-hidden')?.click();
                                            }}
                                            sx={{
                                                border: (theme) => `2px dashed ${isDraggingOver ? theme.palette.primary.main : theme.palette.divider}`,
                                                padding: 3,
                                                textAlign: 'center',
                                                cursor: isSubmitting ? 'default' : 'pointer',
                                                backgroundColor: (theme) => isDraggingOver ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                                transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                                                mb: 2,
                                                opacity: isSubmitting ? 0.7 : 1,
                                            }}
                                        >
                                            <Input
                                                id="fileInput-hidden"
                                                type="file"
                                                inputProps={{ accept: '.zip' }}
                                                onChange={handleFileInputChange}
                                                sx={{ display: 'none' }}
                                                disabled={isSubmitting}
                                            />
                                            {fileName ? (
                                                <>
                                                    <Typography variant="body1" color="textSecondary">Selected file: {fileName}</Typography>
                                                    <Button size="small" onClick={clearFileSelection} sx={{ mt: 1 }} disabled={isSubmitting}>Clear selection</Button>
                                                </>
                                            ) : (
                                                <Typography variant="body1" color="textSecondary">Drag & drop a .zip file here, or click to select</Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                                            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                                            <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>OR</Typography>
                                            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                                        </Box>
                                        <Button fullWidth variant="outlined" startIcon={<Map />} onClick={handleOpenDrawBoundaryDialog} disabled={isSubmitting} sx={{ mb: 2 }}>
                                            Mark Point on Map
                                        </Button>
                                    </>
                                )}
                                {envAnalysisTab === 'bulk' && (
                                    <>
                                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>Upload Sites (.csv)</Typography>
                                        <Box
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleCsvDrop}
                                            onClick={() => {
                                                if (!isSubmitting) document.getElementById('csv-file-input')?.click();
                                            }}
                                            sx={{
                                                border: (theme) => `2px dashed ${isDraggingOver ? theme.palette.primary.main : theme.palette.divider}`,
                                                padding: 3,
                                                textAlign: 'center',
                                                cursor: isSubmitting ? 'default' : 'pointer',
                                                backgroundColor: (theme) => isDraggingOver ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                                transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                                                mb: 1,
                                                opacity: isSubmitting ? 0.7 : 1,
                                            }}
                                        >
                                            <Input
                                                id="csv-file-input"
                                                type="file"
                                                inputProps={{ accept: '.csv' }}
                                                onChange={handleCsvFileInputChange}
                                                sx={{ display: 'none' }}
                                                disabled={isSubmitting}
                                            />
                                            {csvFileName ? (
                                                <>
                                                    <Typography variant="body1" color="textSecondary">Selected file: {csvFileName}</Typography>
                                                    <Button size="small" onClick={clearCsvFileSelection} sx={{ mt: 1 }} disabled={isSubmitting}>Clear selection</Button>
                                                </>
                                            ) : (
                                                <Typography variant="body1" color="textSecondary">Drag & drop a .csv file here, or click to select</Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button variant="text" onClick={handleDownloadSampleCsv} disabled={isSubmitting}>Download Sample CSV</Button>
                                        </Box>
                                        {validationErrors.length > 0 && (
                                            <Box sx={{ my: 1 }}>
                                                <Button color="error" variant="outlined" size="small" onClick={() => setErrorDialog({ open: true, title: 'Invalid CSV Rows', rows: validationErrors })}>
                                                    {`Found ${validationErrors.length} invalid row(s). Click to view details.`}
                                                </Button>
                                            </Box>
                                        )}
                                        {submissionErrors.length > 0 && (
                                            <Box sx={{ my: 1 }}>
                                                <Button color="error" variant="outlined" size="small" onClick={() => setErrorDialog({ open: true, title: 'Submission Errors', rows: submissionErrors })}>
                                                    {`${submissionErrors.length} site(s) failed to submit. Click to view details.`}
                                                </Button>
                                            </Box>
                                        )}
                                        {previewPoints.length > 0 && (
                                            <Box sx={{ height: '400px', mt: 2, position: 'relative' }}>
                                                <SitePreviewMap points={previewPoints} drawType="marker" />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button onClick={() => startTransition(() => navigate(`/project/${projectId}/sites`))} color="inherit" disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || (!formData.carbon_analysis_enabled && !formData.bio_analysis_enabled && !formData.env_analysis_enabled)} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Add />}>
                        {isSubmitting ? 'Adding Site...' : 'Add Site'}
                    </Button>
                </Box>
            </Paper>

            <Dialog fullScreen open={isDrawBoundaryDialogOpen} onClose={handleCloseDrawBoundaryDialog}>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '0' }}>
                    <MapBoxDrawer onCompleteDraw={onCompleteDraw} type={(formData as any).env_analysis_enabled && projectDetails?.has_environmental_module && envAnalysisTab === 'single' ? 'POINT' : 'POLYGON'} />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Button onClick={handleCloseDrawBoundaryDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSubmitDrawBoundary} variant="contained" color="primary" disabled={!((formData as any).boundary || (formData as any).centroid_wkt)}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={errorDialog.open} onClose={() => setErrorDialog(prev => ({ ...prev, open: false }))} maxWidth="md" fullWidth>
                <DialogTitle>{errorDialog.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {errorDialog.title === 'Invalid CSV Rows'
                            ? 'The following rows from your CSV file could not be processed and will be skipped. Please correct them and re-upload if needed.'
                            : 'The following sites could not be created due to an error. Please review the details below.'}
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="invalid rows table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Row Number</TableCell>
                                    <TableCell>{errorDialog.title === 'Invalid CSV Rows' ? 'Row Content' : 'Site Name'}</TableCell>
                                    <TableCell>Error</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {errorDialog.rows.map((row) => (
                                    <TableRow key={row.rowNumber} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">{row.rowNumber}</TableCell>
                                        <TableCell><code>{row.content}</code></TableCell>
                                        <TableCell sx={{ color: 'error.main' }}>{row.reason}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialog(prev => ({ ...prev, open: false }))}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AddSitePage;
